package com.schoolconnect.app.feature.quickannouncements

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.VerticalDivider
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.schoolconnect.app.designsystem.*
import com.schoolconnect.app.designsystem.list.AdminStatusChip

@Composable
internal fun ViewQuickAnnouncementDialog(
    row: QuickAnnouncementRow?,
    onDismiss: () -> Unit
) {
    if (row == null) return

    AppDialogScaffold(
        onDismiss = onDismiss,
        size = AppDialogSize.Wide,
        dialogWidthOverride = 1120.dp,
        maxDialogHeight = 860.dp
    ) {
        AppDialogHeader(
            title = "View Announcement",
            subtitle = "View the announcement details and display duration.",
            titleSuffix = row.id,
            titleSuffixSeparator = " ",
            titleSuffixFontSize = AppDialogInputSize,
            onClose = onDismiss,
            colors = AppDialogDefaultColors
        )
        AppDialogBody(
            compactBreakpoint = 820.dp,
            horizontalPadding = 28.dp,
            verticalPadding = 20.dp
        ) { compact ->
            Surface(
                modifier = Modifier.fillMaxWidth(),
                color = AppInfoBg,
                shape = RoundedCornerShape(8.dp),
                border = BorderStroke(1.dp, AppInfoBorder)
            ) {
                if (compact) {
                    Column(Modifier.padding(18.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                        DetailValue("Created By", row.createdBy, row.createdAt)
                        DetailValue("Last Updated", row.updatedBy, row.updatedAt)
                    }
                } else {
                    Row(Modifier.fillMaxWidth().padding(18.dp), verticalAlignment = Alignment.CenterVertically) {
                        Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(4.dp)) {
                            DetailValue("Created By", row.createdBy, row.createdAt)
                        }
                        VerticalDivider(
                            modifier = Modifier.height(86.dp).width(1.dp),
                            color = AppInfoBorder
                        )
                        Column(Modifier.weight(1f).padding(start = 24.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
                            DetailValue("Last Updated", row.updatedBy, row.updatedAt)
                        }
                    }
                }
            }

            if (compact) {
                Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                    DetailValue("Status", row.status, statusTint = statusColor(row.status))
                    DetailValue("Display Until", row.displayUntil)
                }
            } else {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(48.dp)) {
                    Column(Modifier.weight(1f)) { DetailValue("Status", row.status, statusTint = statusColor(row.status)) }
                    Column(Modifier.weight(1f)) { DetailValue("Display Until", row.displayUntil) }
                }
            }

            AppDialogDivider()
            AppDialogFieldColumn("Announcement", colors = AppDialogDefaultColors) {
                Surface(
                    modifier = Modifier.fillMaxWidth().heightIn(min = 104.dp),
                    color = Color.White,
                    shape = AppDialogFieldShape,
                    border = BorderStroke(1.dp, AppDialogDefaultColors.inputBorder)
                ) {
                    AppText(row.message, color = AppNavy, style = MaterialTheme.typography.bodyMedium.copy(fontSize = AppDialogInputSize), fontWeight = FontWeight.Medium, modifier = Modifier.padding(18.dp))
                }
            }
        }
        AppDialogDivider()
        AppDialogFooter(
            actions = listOf(AppDialogFooterAction("Close", onDismiss, width = 140.dp))
        )
    }
}

@Composable
private fun DetailValue(label: String, value: String, timestamp: String? = null, statusTint: Color? = null) {
    AppText(label, color = AppMuted, style = MaterialTheme.typography.bodyMedium.copy(fontSize = AppDialogLabelSize))
    if (label == "Status") {
        AdminStatusChip(
            label = value,
            contentColor = statusTint ?: statusColor(value),
            backgroundColor = statusBackground(value),
            modifier = Modifier.padding(top = 4.dp)
        )
    } else {
        AppText(value, color = AppNavy, style = MaterialTheme.typography.bodyMedium.copy(fontSize = AppDialogInputSize), fontWeight = FontWeight.Bold)
        if (!timestamp.isNullOrBlank()) AppText(timestamp, color = AppNavy, style = MaterialTheme.typography.bodyMedium.copy(fontSize = AppDialogInputSize))
    }
}

private fun statusColor(status: String): Color = when (status) {
    "Active" -> AppSuccess
    "Expired" -> AppNavy
    else -> AppPrimary
}

private fun statusBackground(status: String): Color = when (status) {
    "Active" -> AppSuccessSoft
    else -> AppPrimarySoft
}
